<script setup lang="ts">
import {LegacyOrder, legacyOrderKey, useLocalization, usePhrases} from '@hantera/portal-app';
import {DropdownButton, DropdownItem} from '@hantera/design-system';
import {inject, ref} from 'vue';
import useBackend from '@/composition/useBackend';
import {onKeydown} from '@/composition/keyboard';
import useApps from '@/composition/use-apps';

const draftChangeMessage = usePhrases('legacyOrderView');
const t = useLocalization();
const { legacyOrders: orders } = useBackend();

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const isSaving = ref(false);
const { onLegacyOrderSaved: onOrderSaved } = useApps();

onKeydown(e => {
  if (e.key === 'z' && e.ctrlKey) {
    e.preventDefault();
    undo();
  }
  if (e.key === 's' && e.ctrlKey) {
    e.preventDefault();
    save();
  }
});


async function save() {
  if (!order.value.draftChanges) {
    return;
  }

  isSaving.value = true;
  try {
    const draftChanges = order.value.draftChanges;
    await orders.save(order.value.orderId);
    await onOrderSaved(order.value, draftChanges);
  }
  finally {
    isSaving.value = false;
  }
}

function undo(preventClose?: () => void) {
  if (!order.value.draftChanges) {
    return;
  }
  if (preventClose && order.value.draftChanges.length > 1) {
    preventClose();
  }

  orders.removeCommandGroup(order.value.orderId, order.value.draftChanges[order.value.draftChanges?.length - 1].commandGroupId);
}

</script>

<template>
  <div class="btn-group">
    <button
      class="btn-primary" @click="save"
      :disabled="!order.draftChanges || order.draftChanges.length === 0 || isSaving"
    >
      Save{{order.draftChanges?.length ?? 0 > 0 ? ` (${order.draftChanges!.length})` : ''}}
    </button>
    <div>
      <DropdownButton
        :disabled="!order.draftChanges || order.draftChanges.length === 0 || isSaving"
      >
        <DropdownItem
          class="save-button__undo"
          @click="undo"
        >
          {{ t('orderView.removeLastChange') }}
        </DropdownItem>
        <DropdownItem class="save-button__unsaved-changes-label">
          {{ t('orderView.draftChanges') }}
        </DropdownItem>
        <DropdownItem
          v-for="change in order.draftChanges"
          :key="change.commandGroupId"
          class="save-button__change"
        >
          <div
            class="legacy-order-header__unsaved-change"
            v-html="draftChangeMessage(change.message, change.fields)"
          ></div>
        </DropdownItem>
      </DropdownButton>
    </div>
  </div>
  
  
</template>

<style>
.save-button__unsaved-changes-label {
  font-weight: bold;
  margin-top: 10px;
  border-top: 1px solid hsl(var(--color-70));
}

.save-button__change {
  border-top: 1px solid hsl(var(--color-70));
}
</style>